import {
    ADDRESS_CLAMP_TO_EDGE,
    FILTER_NEAREST,
    PIXELFORMAT_R8,
    PIXELFORMAT_R16U,
    Asset,
    BoundingBox,
    Color,
    Entity,
    GSplatData,
    GSplatResource,
    Mat4,
    Quat,
    Texture,
    Vec3
} from 'playcanvas';

import type { KeyEasing } from './anim-track';
import { Element, ElementType } from './element';
import { calcInvRotationMat3, defaultGlobalClipBox, type GlobalClipBox } from './global-clip-box';
import { Serializer } from './serializer';
import { vertexShader, fragmentShader, gsplatCenter } from './shaders/splat-shader';
import { State } from './splat-state';
import { Transform } from './transform';
import { TransformPalette } from './transform-palette';

type SplatAnimationDoc = {
    version: 0;
    poses: Array<{
        frame: number;
        position: [number, number, number];
        rotation: [number, number, number, number];
        scale: [number, number, number];
        easing?: KeyEasing;
    }>;
};

const vec = new Vec3();
const veca = new Vec3();
const vecb = new Vec3();
const tmpMat = new Mat4();
const tmpPos = new Vec3();
const tmpScale = new Vec3(1, 1, 1);
const tmpQuat = new Quat();

const boundingPoints =
    [-1, 1].map((x) => {
        return [-1, 1].map((y) => {
            return [-1, 1].map((z) => {
                return [
                    new Vec3(x, y, z), new Vec3(x * 0.75, y, z),
                    new Vec3(x, y, z), new Vec3(x, y * 0.75, z),
                    new Vec3(x, y, z), new Vec3(x, y, z * 0.75)
                ];
            });
        });
    }).flat(3);

class Splat extends Element {
    asset: Asset;
    splatData: GSplatData;
    numSplats = 0;
    numDeleted = 0;
    numLocked = 0;
    numSelected = 0;
    entity: Entity;
    bindEntity: Entity;
    contentEntity: Entity;
    private importNormalization = new Quat();
    changedCounter = 0;
    stateTexture: Texture;
    transformTexture: Texture;
    selectionBoundStorage: BoundingBox;
    localBoundStorage: BoundingBox;
    worldBoundStorage: BoundingBox;

    _visible = true;
    transformPalette: TransformPalette;

    selectionAlpha = 1;

    _name = '';
    _tintClr = new Color(1, 1, 1);
    _temperature = 0;
    _saturation = 1;
    _brightness = 0;
    _blackPoint = 0;
    _whitePoint = 1;
    _transparency = 1;

    measurePoints: Vec3[] = [];
    measureSelection = -1;

    rebuildMaterial: (bands: number) => void;

    constructor(asset: Asset, orientation: Vec3 | Quat) {
        super(ElementType.splat);

        const splatResource = asset.resource as GSplatResource;
        const splatData = splatResource.gsplatData;
        const { device } = splatResource;

        this._name = ((asset.file as any)?.filename as string | undefined) ?? asset.name ?? 'Splat';
        this.asset = asset;
        this.splatData = splatData as GSplatData;
        this.numSplats = splatData.numSplats;

        // Root entity holds user-controlled TRS.
        this.entity = new Entity('splatEntity');

        // Bind entity holds static import-time normalization.
        this.bindEntity = new Entity('splatBind');
        if (typeof (orientation as Quat).w === 'number') {
            this.importNormalization.copy(orientation as Quat);
        } else {
            this.importNormalization.setFromEulerAngles(orientation.x, orientation.y, orientation.z);
        }
        this.bindEntity.setLocalRotation(this.importNormalization);
        this.entity.addChild(this.bindEntity);

        // Payload entity holds the gsplat component.
        this.contentEntity = new Entity('splatContent');
        this.contentEntity.addComponent('gsplat', { asset });
        this.bindEntity.addChild(this.contentEntity);

        const instance = this.contentEntity.gsplat.instance;

        // added per-splat state channel
        // bit 1: selected
        // bit 2: deleted
        // bit 3: locked
        if (!this.splatData.getProp('state')) {
            this.splatData.getElement('vertex').properties.push({
                type: 'uchar',
                name: 'state',
                storage: new Uint8Array(this.splatData.numSplats),
                byteSize: 1
            });
        }

        // per-splat transform matrix (palette index). Avoid duplicating this prop if we're
        // constructing a splat from an already-augmented GSplatData (e.g. true duplication).
        if (!this.splatData.getProp('transform')) {
            this.splatData.getElement('vertex').properties.push({
                type: 'ushort',
                name: 'transform',
                storage: new Uint16Array(this.splatData.numSplats),
                byteSize: 2
            });
        }

        const { x: width, y: height } = (splatResource as any).textureDimensions;

        // pack spherical harmonic data
        const createTexture = (name: string, format: number) => {
            return new Texture(device, {
                name: name,
                width: width,
                height: height,
                format: format,
                mipmaps: false,
                minFilter: FILTER_NEAREST,
                magFilter: FILTER_NEAREST,
                addressU: ADDRESS_CLAMP_TO_EDGE,
                addressV: ADDRESS_CLAMP_TO_EDGE
            });
        };

        // create the state texture
        this.stateTexture = createTexture('splatState', PIXELFORMAT_R8);
        this.transformTexture = createTexture('splatTransform', PIXELFORMAT_R16U);

        // create the transform palette
        this.transformPalette = new TransformPalette(device);

        this.rebuildMaterial = (bands: number) => {
            const { material } = instance;
            const { glsl } = material.shaderChunks;
            glsl.set('gsplatVS', vertexShader);
            glsl.set('gsplatPS', fragmentShader);
            glsl.set('gsplatCenterVS', gsplatCenter);

            material.setDefine('SH_BANDS', `${Math.min(bands, (instance.resource as GSplatResource).shBands)}`);
            material.setParameter('splatState', this.stateTexture);
            material.setParameter('splatTransform', this.transformTexture);
            material.update();
        };

        this.selectionBoundStorage = new BoundingBox();
        this.localBoundStorage = instance.resource.aabb;
        // @ts-ignore
        this.worldBoundStorage = instance.meshInstance._aabb;

        // @ts-ignore
        instance.meshInstance._updateAabb = false;

        // when sort changes, re-render the scene
        instance.sorter.on('updated', () => {
            this.changedCounter++;
        });
    }

    // Deep-clone GSplatData, including all vertex property storage arrays.
    // This is the core primitive for “true duplication” (C.5).
    cloneSplatData(): GSplatData {
        const src = this.splatData;
        const srcAny: any = src as any;
        const elements = (srcAny?.elements ?? srcAny?._elements ?? null) as any[] | null;

        const cloneStorage = (storage: any) => {
            if (!storage || typeof storage !== 'object') return storage;
            if (typeof storage.slice === 'function') return storage.slice(0);
            const Ctor: any = storage.constructor;
            return new Ctor(storage);
        };

        const cloneElement = (el: any) => {
            const props = (el?.properties ?? []) as any[];
            return {
                name: String(el?.name ?? 'vertex'),
                count: Number(el?.count ?? src.numSplats),
                properties: props.map(p => ({
                    type: p.type,
                    name: p.name,
                    storage: cloneStorage(p.storage),
                    byteSize: p.byteSize
                }))
            };
        };

        if (Array.isArray(elements) && elements.length) {
            return new GSplatData(elements.map(cloneElement));
        }

        const vertex = (src as any).getElement?.('vertex');
        if (!vertex) {
            throw new Error('cloneSplatData failed: missing vertex element');
        }
        return new GSplatData([cloneElement(vertex)]);
    }

    // Expose vertex property descriptors (name/type/byteSize) and their underlying storage arrays.
    // Used by advanced edit ops (duplicate/split) to clone/subset per-splat attributes.
    // Returned objects reference the underlying storage; callers MUST treat them as read-only.
    getVertexProperties(): { name: string; type: string; byteSize: number; storage: ArrayBufferView }[] {
        const vertex = (this.splatData as any).getElement?.('vertex');
        const props = (vertex?.properties ?? []) as any[];
        return props.map(p => ({
            name: String(p?.name ?? ''),
            type: String(p?.type ?? ''),
            byteSize: Number(p?.byteSize ?? 0),
            storage: p?.storage as ArrayBufferView
        }));
    }

    // C.6: Convenience accessor used by split/duplicate operations.
    // Returns a map of vertex property name -> underlying TypedArray storage.
    // The returned arrays are the live underlying storages; treat them as read-only.
    getVertexPropertyArrays(): Map<string, ArrayBufferView> {
        const vertex = (this.splatData as any).getElement?.('vertex');
        const props = (vertex?.properties ?? []) as any[];

        const result = new Map<string, ArrayBufferView>();
        for (const p of props) {
            const name = String(p?.name ?? '');
            const storage = p?.storage as ArrayBufferView | undefined;
            if (name && storage) result.set(name, storage);
        }
        return result;
    }

    destroy() {
        super.destroy();
        this.entity.destroy();
        this.asset.registry.remove(this.asset);
        this.asset.unload();
    }

    async updateState(changedState = State.selected) {
        const state = this.splatData.getProp('state') as Uint8Array;

        // write state data to gpu texture
        const data = this.stateTexture.lock();
        data.set(state);
        this.stateTexture.unlock();

        let numSelected = 0;
        let numLocked = 0;
        let numDeleted = 0;

        for (let i = 0; i < state.length; ++i) {
            const s = state[i];
            if (s & State.deleted) {
                numDeleted++;
            } else if (s & State.locked) {
                numLocked++;
            } else if (s & State.selected) {
                numSelected++;
            }
        }

        this.numSplats = state.length - numDeleted;
        this.numLocked = numLocked;
        this.numSelected = numSelected;
        this.numDeleted = numDeleted;

        // handle splats being added or removed
        if (changedState & State.deleted) {
            await this.updateSorting();
        } else {
            await this.updateLocalBounds();
        }

        this.scene.forceRender = true;
        this.scene.events.fire('splat.stateChanged', this);
    }

    async updatePositions() {
        const data = await this.scene.dataProcessor.calcPositions(this);

        // update the splat centers which are used for render-time sorting
        const state = this.splatData.getProp('state') as Uint8Array;
        const { sorter } = this.contentEntity.gsplat.instance;
        const { centers } = sorter;
        for (let i = 0; i < this.splatData.numSplats; ++i) {
            if (state[i] === State.selected) {
                centers[i * 3 + 0] = data[i * 4];
                centers[i * 3 + 1] = data[i * 4 + 1];
                centers[i * 3 + 2] = data[i * 4 + 2];
            }
        }

        await this.updateSorting();

        this.scene.forceRender = true;
        this.scene.events.fire('splat.positionsChanged', this);
    }

    async updateSorting() {
        const state = this.splatData.getProp('state') as Uint8Array;

        let mapping;

        // create a sorter mapping to remove deleted splats
        if (this.numSplats !== state.length) {
            mapping = new Uint32Array(this.numSplats);
            let idx = 0;
            for (let i = 0; i < state.length; ++i) {
                if ((state[i] & State.deleted) === 0) {
                    mapping[idx++] = i;
                }
            }
        }

        // update sorting instance
        this.contentEntity.gsplat.instance.sorter.setMapping(mapping);

        // recalculate bounds after sorting changes
        await this.updateLocalBounds();
    }

    get worldTransform() {
        // Render-time/world-space transform includes both user TRS and import normalization.
        return this.contentEntity.getWorldTransform();
    }

    get animationControllerEntity() {
        return this.entity;
    }

    get animationBindEntity() {
        return this.bindEntity;
    }

    get animationPayloadEntity() {
        return this.contentEntity;
    }

    get importNormalizationRotation() {
        // Return a copy to prevent accidental external mutation.
        return new Quat(this.importNormalization.x, this.importNormalization.y, this.importNormalization.z, this.importNormalization.w);
    }

    setImportNormalization(rotation: Quat) {
        this.importNormalization.copy(rotation);
        this.bindEntity.setLocalRotation(this.importNormalization);
        this.updateWorldBound();
        this.scene.forceRender = true;
        this.scene.events.fire('splat.normalizationChanged', this);
    }

    setImportNormalizationEuler(eulerDegrees: Vec3) {
        tmpQuat.setFromEulerAngles(eulerDegrees.x, eulerDegrees.y, eulerDegrees.z);
        this.setImportNormalization(tmpQuat);
    }

    getImportNormalizationInverseMatrix(result: Mat4) {
        // Import normalization is applied as a local rotation on bindEntity.
        // For exporting in pre-normalized space we need its inverse.
        tmpQuat.copy(this.importNormalization).invert();
        result.setTRS(tmpPos.set(0, 0, 0), tmpQuat, tmpScale);
        return result;
    }

    set name(newName: string) {
        if (newName !== this.name) {
            this._name = newName;
            this.scene.events.fire('splat.name', this);
        }
    }

    get name() {
        return this._name;
    }

    get filename() {
        return ((this.asset.file as any)?.filename as string | undefined) ?? this.asset.name ?? this._name ?? 'Splat';
    }

    calcSplatWorldPosition(splatId: number, result: Vec3) {
        if (splatId >= this.splatData.numSplats) {
            return false;
        }

        // use centers data, which are updated when edits occur
        const { sorter } = this.contentEntity.gsplat.instance;
        const { centers } = sorter;

        result.set(
            centers[splatId * 3 + 0],
            centers[splatId * 3 + 1],
            centers[splatId * 3 + 2]
        );

        this.worldTransform.transformPoint(result, result);

        return true;
    }

    async add() {
        // add the entity to the scene
        this.scene.contentRoot.addChild(this.entity);

        // assign splat to the dedicated splat layer (rendered by splat camera with MRT)
        this.contentEntity.gsplat.layers = [this.scene.splatLayer.id];

        this.scene.events.on('view.bands', this.rebuildMaterial, this);
        this.rebuildMaterial(this.scene.events.invoke('view.bands'));

        // we must update state in case the state data was loaded from ply
        await this.updateState();
    }

    remove() {
        this.scene.events.off('view.bands', this.rebuildMaterial, this);

        this.scene.contentRoot.removeChild(this.entity);
        this.scene.boundDirty = true;
    }

    serialize(serializer: Serializer) {
        serializer.packa(this.worldTransform.data);
        serializer.pack(this.changedCounter);
        serializer.pack(this.visible);
        serializer.pack(this.tintClr.r, this.tintClr.g, this.tintClr.b);
        serializer.pack(this.temperature, this.saturation, this.brightness, this.blackPoint, this.whitePoint, this.transparency);
    }

    onPreRender() {
        const events = this.scene.events;
        const selected = this.scene.camera.renderOverlays && events.invoke('selection') === this;
        const cameraMode = events.invoke('camera.mode');
        const cameraOverlay = events.invoke('camera.overlay');

        // configure rings rendering
        const material = this.contentEntity.gsplat.instance.material;
        material.setParameter('outlineMode', events.invoke('view.outlineSelection') ? 1 : 0);
        material.setParameter('ringSize', (selected && cameraOverlay && cameraMode === 'rings') ? 0.04 : 0);

        // configure colors
        const selectedClr = events.invoke('selectedClr');
        const unselectedClr = events.invoke('unselectedClr');
        const lockedClr = events.invoke('lockedClr');

        if (!selected) {
            material.setParameter('selectedClr', [0, 0, 0, 0]);
        } else if (events.invoke('view.outlineSelection')) {
            material.setParameter('selectedClr', [0, 0, 0, 0]);
        } else {
            material.setParameter('selectedClr', [selectedClr.r, selectedClr.g, selectedClr.b, selectedClr.a * this.selectionAlpha]);
        }
        material.setParameter('unselectedClr', [unselectedClr.r, unselectedClr.g, unselectedClr.b, unselectedClr.a]);
        material.setParameter('lockedClr', [lockedClr.r, lockedClr.g, lockedClr.b, lockedClr.a]);

        // combine black pointer, white point and brightness
        const offset = -this.blackPoint + this.brightness;
        const scale = 1 / (this.whitePoint - this.blackPoint);

        material.setParameter('clrOffset', [offset, offset, offset]);
        material.setParameter('clrScale', [
            scale * this.tintClr.r * (1 + this.temperature),
            scale * this.tintClr.g,
            scale * this.tintClr.b * (1 - this.temperature),
            this.transparency
        ]);

        const clip = (events.invoke('clipBox') ?? defaultGlobalClipBox()) as GlobalClipBox;
        const clipMode = (events.invoke('clipBox.mode') ?? 'shader') as string;
        const clipEnabled = !!clip?.enabled && clipMode === 'shader';
        const invRot = calcInvRotationMat3(clip.rotation);

        material.setParameter('clipBoxEnabled', clipEnabled ? 1 : 0);
        material.setParameter('clipBoxCenter', [clip.center.x, clip.center.y, clip.center.z]);
        material.setParameter('clipBoxHalf', [clip.halfExtents.x, clip.halfExtents.y, clip.halfExtents.z]);
        material.setParameter('clipBoxInvRot', invRot.data);

        material.setParameter('saturation', this.saturation);
        material.setParameter('transformPalette', this.transformPalette.texture);

        if (this.visible && selected) {
            // render bounding box
            if (events.invoke('camera.bound')) {
                const bound = this.localBound;
                const scale = new Mat4().setTRS(bound.center, Quat.IDENTITY, bound.halfExtents);
                scale.mul2(this.worldTransform, scale);

                for (let i = 0; i < boundingPoints.length / 2; i++) {
                    const a = boundingPoints[i * 2];
                    const b = boundingPoints[i * 2 + 1];
                    scale.transformPoint(a, veca);
                    scale.transformPoint(b, vecb);

                    this.scene.app.drawLine(veca, vecb, Color.WHITE, true, this.scene.worldLayer);
                }
            }
        }

        this.entity.enabled = this.visible;
    }

    focalPoint() {
        // GSplatData has a function for calculating an weighted average of the splat positions
        // to get a focal point for the camera, but we use bound center instead
        return this.worldBound.center;
    }

    move(position?: Vec3, rotation?: Quat, scale?: Vec3) {
        const entity = this.animationControllerEntity;
        if (position) {
            entity.setLocalPosition(position);
        }
        if (rotation) {
            entity.setLocalRotation(rotation);
        }
        if (scale) {
            entity.setLocalScale(scale);
        }

        this.updateWorldBound();

        this.scene.events.fire('splat.moved', this);
    }

    // calculate both selection and local bounds (async, callers must await)
    async updateLocalBounds(): Promise<void> {
        await this.scene.dataProcessor.calcBound(this, this.selectionBoundStorage, this.localBoundStorage);
        this.updateWorldBound();
    }

    // update world bound from local bound (synchronous)
    private updateWorldBound() {
        this.worldBoundStorage.setFromTransformedAabb(this.localBoundStorage, this.worldTransform);
        this.scene.boundDirty = true;
    }

    // get the selection bound
    get selectionBound() {
        return this.selectionBoundStorage;
    }

    // get local space bound
    get localBound() {
        return this.localBoundStorage;
    }

    // get world space bound
    get worldBound() {
        return this.worldBoundStorage;
    }

    set visible(value: boolean) {
        if (value !== this.visible) {
            this._visible = value;
            this.scene?.events.fire('splat.visibility', this);
        }
    }

    get visible() {
        return this._visible;
    }

    set tintClr(value: Color) {
        if (!this._tintClr.equals(value)) {
            this._tintClr.set(value.r, value.g, value.b);
            this.scene.events.fire('splat.tintClr', this);
        }
    }

    get tintClr() {
        return this._tintClr;
    }

    set temperature(value: number) {
        if (value !== this._temperature) {
            this._temperature = value;
            this.scene.events.fire('splat.temperature', this);
        }
    }

    get temperature() {
        return this._temperature;
    }

    set saturation(value: number) {
        if (value !== this._saturation) {
            this._saturation = value;
            this.scene.events.fire('splat.saturation', this);
        }
    }

    get saturation() {
        return this._saturation;
    }

    set brightness(value: number) {
        if (value !== this._brightness) {
            this._brightness = value;
            this.scene.events.fire('splat.brightness', this);
        }
    }

    get brightness() {
        return this._brightness;
    }

    set blackPoint(value: number) {
        if (value !== this._blackPoint) {
            this._blackPoint = value;
            this.scene.events.fire('splat.blackPoint', this);
        }
    }

    get blackPoint() {
        return this._blackPoint;
    }

    set whitePoint(value: number) {
        if (value !== this._whitePoint) {
            this._whitePoint = value;
            this.scene.events.fire('splat.whitePoint', this);
        }
    }

    get whitePoint() {
        return this._whitePoint;
    }

    set transparency(value: number) {
        if (value !== this._transparency) {
            this._transparency = value;
            this.scene.events.fire('splat.transparency', this);
        }
    }

    get transparency() {
        return this._transparency;
    }

    // get pivot position/rotation/scale (caller should have awaited operation that changed data)
    getPivot(mode: 'center' | 'boundCenter', selection: boolean, result: Transform) {
        const entity = this.animationControllerEntity;
        switch (mode) {
            case 'center':
                result.set(entity.getLocalPosition(), entity.getLocalRotation(), entity.getLocalScale());
                break;
            case 'boundCenter': {
                const bound = selection ? this.selectionBound : this.localBound;
                // bound.center is in payload local space. Convert using controllerLocal * bindLocal.
                tmpMat.mul2(entity.getLocalTransform(), this.animationBindEntity.getLocalTransform());
                tmpMat.transformPoint(bound.center, vec);
                result.set(vec, entity.getLocalRotation(), entity.getLocalScale());
                break;
            }
        }
    }

    docSerialize() {
        const pack3 = (v: Vec3) => [v.x, v.y, v.z];
        const pack4 = (q: Quat) => [q.x, q.y, q.z, q.w];
        const packC = (c: Color) => [c.r, c.g, c.b, c.a];
        const track = this.scene?.events.invoke('elementAnim.track', this) ?? null;
        const poses = track?.getPoses() ?? [];

        return {
            name: this.name,
            position: pack3(this.animationControllerEntity.getLocalPosition()),
            rotation: pack4(this.animationControllerEntity.getLocalRotation()),
            scale: pack3(this.animationControllerEntity.getLocalScale()),
            animation: poses.length > 0 ? {
                version: 0 as const,
                poses
            } satisfies SplatAnimationDoc : undefined,
            visible: this.visible,
            tintClr: packC(this.tintClr),
            temperature: this.temperature,
            saturation: this.saturation,
            brightness: this.brightness,
            blackPoint: this.blackPoint,
            whitePoint: this.whitePoint,
            transparency: this.transparency
        };
    }

    docDeserialize(doc: any) {
        const { name, position, rotation, scale, animation, visible, tintClr, temperature, saturation, brightness, blackPoint, whitePoint, transparency } = doc;

        this.name = name;
        this.move(new Vec3(position), new Quat(rotation), new Vec3(scale));
        if (animation?.version === 0) {
            const track = this.scene?.events.invoke('elementAnim.track', this) ?? null;
            track?.loadPoses(animation.poses);
        }
        this.visible = visible;
        this.tintClr = new Color(tintClr[0], tintClr[1], tintClr[2], tintClr[3]);
        this.temperature = temperature ?? 0;
        this.saturation = saturation ?? 1;
        this.brightness = brightness;
        this.blackPoint = blackPoint;
        this.whitePoint = whitePoint;
        this.transparency = transparency;
    }
}

export { Splat };
